import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { venueUrl } from "../utils/venueUrl";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { MapPin, Star, Search, List, Map } from "lucide-react";
import axios from "axios";
import "leaflet/dist/leaflet.css";

export default function VenueMapPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const L = (obj, f) => (lang === "el" ? (obj?.[`${f}_el`] || obj?.[f] || "") : (obj?.[f] || ""));
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("map");
  const [selectedVenue, setSelectedVenue] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const res = await axios.get(`${API}/venues?limit=100`);
        setVenues(res.data);
      } catch (error) { console.error(error); }
      finally { setLoading(false); }
    };
    fetchVenues();
  }, []);

  useEffect(() => {
    if (viewMode !== "map" || !mapRef.current || loading) return;

    const initMap = async () => {
      const L = (await import("leaflet")).default;

      // Fix default marker icons
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
        iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
        shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
      });

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }

      const map = L.map(mapRef.current).setView([35.1264, 33.4299], 9); // Cyprus center
      mapInstanceRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 18,
      }).addTo(map);

      // Clear old markers
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];

      const filtered = filterVenues(venues);

      filtered.forEach((venue) => {
        if (venue.latitude && venue.longitude) {
          const marker = L.marker([venue.latitude, venue.longitude])
            .addTo(map)
            .bindPopup(`
              <div style="min-width:180px">
                <strong>${venue.name}</strong><br/>
                <span style="color:#666;font-size:12px">${venue.short_description || ""}</span><br/>
                <a href="${venueUrl(venue)}" style="color:#2563eb;font-size:12px">View Details</a>
              </div>
            `);
          marker.on("click", () => setSelectedVenue(venue));
          markersRef.current.push(marker);
        }
      });

      // If no venues have coordinates, add city markers
      if (markersRef.current.length === 0) {
        const cities = [
          { name: "Nicosia", lat: 35.1856, lng: 33.3823 },
          { name: "Limassol", lat: 34.6841, lng: 33.0379 },
          { name: "Paphos", lat: 34.7754, lng: 32.4245 },
          { name: "Larnaca", lat: 34.9229, lng: 33.6233 },
          { name: "Ayia Napa", lat: 34.9882, lng: 33.9998 },
        ];
        cities.forEach((city) => {
          const count = filtered.filter((v) => v.city_id === city.name.toLowerCase()).length;
          const marker = L.marker([city.lat, city.lng])
            .addTo(map)
            .bindPopup(`<strong>${city.name}</strong><br/>${count} venues`);
          markersRef.current.push(marker);
        });
      }
    };

    initMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [viewMode, venues, loading, search]);

  const filterVenues = (v) => {
    if (!search) return v;
    const s = search.toLowerCase();
    return v.filter((venue) =>
      venue.name.toLowerCase().includes(s) ||
      venue.city_id?.toLowerCase().includes(s) ||
      venue.short_description?.toLowerCase().includes(s)
    );
  };

  const filtered = filterVenues(venues);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-20">
        {/* Search bar */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("map_page:search_placeholder", { defaultValue: "Search venues, cities..." })}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="map-search-input"
                />
              </div>
              <div className="flex gap-1 bg-muted rounded-lg p-1">
                <Button
                  variant={viewMode === "map" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("map")}
                  data-testid="map-view-toggle"
                >
                  <Map className="h-4 w-4 mr-1" /> {t("map_page:map", { defaultValue: "Map" })}
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  data-testid="list-view-toggle"
                >
                  <List className="h-4 w-4 mr-1" /> {t("map_page:list", { defaultValue: "List" })}
                </Button>
              </div>
              <span className="text-sm text-muted-foreground">
                {t("venues_page:count", { count: filtered.length })}
              </span>
            </div>
          </div>
        </div>

        {viewMode === "map" ? (
          <div className="flex" style={{ height: "calc(100vh - 140px)" }}>
            {/* Map */}
            <div className="flex-1 relative" data-testid="venue-map-container">
              <div ref={mapRef} className="w-full h-full" />
            </div>
            {/* Side panel */}
            {selectedVenue && (
              <div className="w-80 bg-card border-l border-border p-5 overflow-y-auto" data-testid="map-venue-detail-panel">
                {selectedVenue.hero_image && (
                  <img src={selectedVenue.hero_image} alt={L(selectedVenue, "name")} className="w-full h-40 object-cover rounded-lg mb-4" />
                )}
                <h3 className="text-lg font-medium text-foreground">{L(selectedVenue, "name")}</h3>
                <p className="text-sm text-muted-foreground mt-1 mb-3">{L(selectedVenue, "short_description")}</p>
                {selectedVenue.address && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                    <MapPin className="h-3.5 w-3.5" /> {L(selectedVenue, "address")}
                  </p>
                )}
                {selectedVenue.average_rating > 0 && (
                  <p className="text-sm flex items-center gap-1 mb-4">
                    <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                    {selectedVenue.average_rating.toFixed(1)}
                  </p>
                )}
                <Link to={venueUrl(selectedVenue)}>
                  <Button className="w-full" data-testid="map-venue-view-btn">
                    {t("map_page:view_details", { defaultValue: "View Details" })}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((venue) => (
                <Link key={venue.venue_id} to={venueUrl(venue)} className="group" data-testid={`map-venue-card-${venue.venue_id}`}>
                  <div className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="h-48 bg-muted relative overflow-hidden">
                      {venue.hero_image ? (
                        <img src={venue.hero_image} alt={L(venue, "name")} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="flex items-center justify-center h-full"><MapPin className="h-8 w-8 text-muted-foreground/30" /></div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">{L(venue, "name")}</h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{L(venue, "short_description")}</p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-muted-foreground">{L(venue, "city_name") || venue.city_id}</span>
                        {venue.average_rating > 0 && (
                          <span className="text-xs flex items-center gap-1">
                            <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                            {venue.average_rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      {viewMode === "list" && <Footer />}
    </div>
  );
}
